import { Letter } from "src/js/stores/letter/store";
import Feature from "src/js/stores/user/Feature";
import hasFeature from "src/js/stores/user/actions/hasFeature";

const getHtmlLetterPage = (mainContent: string) => {
  const usAccess = hasFeature(Feature.usAccess);
  const pagePadding = usAccess ? "0.5in" : "20mm";
  const leftPadding = usAccess ? "0.5in" : "22mm";

  const pageStyle = `.page { width: 100%; height: 100%; background: white; position: relative; padding: ${pagePadding}; padding-left: ${leftPadding}}`;
  const style = `<style type = "text/css">html, body { margin: 0; padding: 0; width: 100%; height: 100%; font-family: "Inter", Arial, sans-serif; font-size: 11pt; } * { box-sizing: border-box; -moz-box-sizing: border-box; } ${pageStyle} .letter-image { width: 100%; height: 100%; object-fit: contain; } .no-margins { margin: 0; }</style>`;
  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>Letter</title>${style}</head><body>${mainContent}</body></html>`;
};

const buildSignatureTemplate = ({
  signOff,
  signature,
  contactName,
  contactEmail,
  contactPhone,
}: {
  signOff?: string;
  signature?: { file: File; url?: string; s3ObjectKey?: string };
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
}): string => {
  const signatureTemplateElements = [];
  signatureTemplateElements.push(
    signOff ? `<p class="no-margins">{{sign_off}}</p>` : "",
    signature
      ? `<div style="display: flex; align-items: center; justify-content: center; height: 94px; width: 156px; margin-top: 0; overflow: hidden;"><img class="letter-image" src={{signature_url}} /></div>`
      : "",
    contactName ? `<p class="no-margins">{{contact_name}}</p>` : "",
    contactEmail ? `<p class="no-margins">{{contact_email}}</p>` : "",
    contactPhone ? `<p class="no-margins">{{contact_phone}}</p>` : ""
  );
  return signatureTemplateElements.join("");
};

const buildFirstPageTemplate = ({
  logo,
  propertyAddress,
  signaturePage,
  signatureTemplate,
  footerTemplate,
  hideRecipientAddress,
  recipientProvince,
}: {
  logo?: { file: File; url?: string; s3ObjectKey?: string };
  propertyAddress: string;
  signaturePage?: number;
  signatureTemplate: string;
  footerTemplate: string;
  hideRecipientAddress?: boolean;
  recipientProvince?: string;
}): string => {
  const usAccess = hasFeature(Feature.usAccess);

  const firstPageContentElements = [];
  const visibility = hideRecipientAddress ? "hidden" : "visible";
  const addressDiv = usAccess
    ? `<div style="margin-top: -0.3in; width: 3.25in; min-height: 2.3in; visibility: ${visibility}">`
    : `<div style="margin-top: 19mm; width: 90mm; min-height: 45mm; visibility: ${visibility}">`;
  const contentDiv = usAccess ? `<div>` : `<div style="margin-top: 76px">`;

  firstPageContentElements.push(
    `<div class="page">`,
    `<div style="display: flex; justify-content: space-between;">`,
    addressDiv,
    `<span>{{recipient_name}}</span><br />`,
    `<span>{{recipient_address_line_1}}</span><br />`,
    `<span>{{recipient_city}}</span><br />`,
    recipientProvince ? `<span>{{recipient_province}}</span><br />` : "",
    `<span>{{recipient_post_code}}</span>`,
    `</div>`,
    logo
      ? `<div style="max-width: 156px; max-height: 156px; overflow: hidden;"><img class="letter-image" src={{logo_url}} /></div>`
      : "",
    `</div>`,
    contentDiv,
    `<p>{{sent_date}}</p>`,
    `<p>{{greeting}} {{recipient_name}},</p>`,
    propertyAddress ? `<p><strong>Property address: </strong>{{property_address}}</p>` : "",
    !usAccess ? `<p><strong>Title number: </strong>{{title}}</p>` : "",
    `<p>{{body_text_page_1}}</p>`,
    signaturePage === 1 ? `<br />${signatureTemplate}` : "",
    footerTemplate,
    `</div>`,
    `</div>`
  );
  return firstPageContentElements.join("");
};

const buildFooterTemplate = ({
  vatRegistrationNumber,
  companyRegistrationNumber,
  senderProvince,
}: Pick<Letter, "companyRegistrationNumber" | "vatRegistrationNumber" | "senderProvince">) => {
  const usAccess = hasFeature(Feature.usAccess);

  if (usAccess) {
    return "";
  }
  const footerHtml = [];

  footerHtml.push(`<div style="bottom: 2rem; position: absolute;">`);
  if (vatRegistrationNumber) {
    footerHtml.push(`<span style="margin-right: 1rem;">`);
    footerHtml.push("VAT No: {{vat_registration_number}}");
    footerHtml.push("</span>");
  }

  if (companyRegistrationNumber) {
    footerHtml.push(`<span style="margin-right: 1rem;">`);
    footerHtml.push("Company No: {{company_registration_number}}");
    footerHtml.push("</span>");
  }

  const senderProvinceTemplateVariable = senderProvince ? "{{ sender_province }}, " : "";

  footerHtml.push(
    `<br /><span>{{ sender_name }}, {{sender_address_line_1}}, {{ sender_city }}, ${senderProvinceTemplateVariable}{{ sender_post_code }}</span>`
  );

  footerHtml.push("</div>");

  return footerHtml.join("");
};

const buildSecondPageTemplate = ({
  showSiteBoundary,
  bodyTextPage2,
  signaturePage,
  signatureTemplate,
  footerTemplate,
}: {
  showSiteBoundary: boolean;
  bodyTextPage2?: string;
  signaturePage?: number;
  signatureTemplate: string;
  footerTemplate: string;
}) => {
  const usAccess = hasFeature(Feature.usAccess);

  const secondPageContentElements = [];
  const contentDiv = usAccess ? `<div style="padding: 0.5in;">` : `<div>`;
  secondPageContentElements.push(
    `<div class="page">`,
    contentDiv,
    showSiteBoundary
      ? `<div style="display: flex; justify-content: center; max-width: 420px; max-height: 330px; margin: 0 auto;"><img class="letter-image" src={{site_boundary_image}} /></div>`
      : "",
    bodyTextPage2 ? `<p>{{body_text_page_2}}</p>` : "",
    signaturePage === 2 ? `<br />${signatureTemplate}` : "",
    footerTemplate,
    `</div>`,
    `</div>`
  );
  return secondPageContentElements.join("");
};

export const convertFormDataToHtml = (
  {
    logo,
    signOff,
    signature,
    contactName,
    contactEmail,
    contactPhone,
    bodyTextPage2,
    signaturePage,
    showSiteBoundary,
    vatRegistrationNumber,
    companyRegistrationNumber,
    senderProvince,
  }: Letter,
  {
    pageCount,
    propertyAddress,
    hideRecipientAddress,
    recipientProvince,
  }: {
    pageCount: number;
    propertyAddress: string;
    hideRecipientAddress?: boolean;
    recipientProvince?: string;
  }
) => {
  let signatureTemplate = "";

  if (signaturePage) {
    signatureTemplate = buildSignatureTemplate({
      signOff,
      signature,
      contactName,
      contactEmail,
      contactPhone,
    });
  }

  const footerTemplate = buildFooterTemplate({
    vatRegistrationNumber,
    companyRegistrationNumber,
    senderProvince,
  });

  const firstPageContent = buildFirstPageTemplate({
    logo,
    propertyAddress,
    signaturePage,
    signatureTemplate,
    footerTemplate,
    hideRecipientAddress,
    recipientProvince,
  });

  if (pageCount > 1) {
    const secondPageContent = buildSecondPageTemplate({
      showSiteBoundary,
      bodyTextPage2,
      signaturePage,
      signatureTemplate,
      footerTemplate,
    });

    const htmlTemplateBody = firstPageContent.concat(secondPageContent);
    return getHtmlLetterPage(htmlTemplateBody);
  }

  return getHtmlLetterPage(firstPageContent);
};
